import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { KollabrAttributes, VerifiedBadge } from '../customers/KollabrProfile';
import { useCustomer } from '@/api/customers';
import { useTeams } from '@/api/teams';
import { useTicketsInfinite, useUpdateTicket } from '@/api/tickets';
import { useAllUsers } from '@/api/users';
import type { Ticket, UpdateTicketInput } from '@/api/types';
import { Avatar, Badge, Spinner, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelative,
  formatTokens,
  humanize,
} from '@/lib/format';
import { useTicketAiUsage } from '@/api/ai';
import { AI_FEATURE_LABEL } from '../platform/meta';
import { CHANNEL_LABEL, PRIORITIES, PRIORITY_LABEL, STATUSES, STATUS_HELP, STATUS_LABEL } from './meta';
import { SlaBadge, StatusBadge } from './TicketBadges';

const selectCls =
  'h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-800 disabled:bg-slate-50 disabled:text-slate-500';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800">{children}</dd>
    </div>
  );
}

export function TicketSidebar({ ticket }: { ticket: Ticket }) {
  const { user, can } = useAuth();
  const toast = useToast();
  const update = useUpdateTicket();
  const users = useAllUsers();
  const teams = useTeams();
  const canWrite = can('update', 'Ticket');
  const canAssign = can('assign', 'Ticket');

  const save = (input: UpdateTicketInput, done?: string) =>
    update.mutate(
      { id: ticket.id, input },
      { onSuccess: () => done && toast.success(done), onError: (e) => toast.error(e.message) },
    );

  // Without tickets:assign, agents may only take an unassigned ticket or release their own.
  const assignable = (users.data ?? []).filter(
    (u) => canAssign || u.id === user.id || u.id === ticket.assigneeId,
  );
  const lockedAssignee = !canAssign && ticket.assigneeId !== null && ticket.assigneeId !== user.id;
  const frtMinutes = ticket.firstResponseAt
    ? (new Date(ticket.firstResponseAt).getTime() - new Date(ticket.createdAt).getTime()) / 60_000
    : null;

  return (
    <div className="space-y-6">
      <section aria-labelledby="props-h" className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 id="props-h" className="text-sm font-bold">
            Properties
          </h2>
          {update.isPending && <Spinner size="sm" label="Saving" />}
        </div>
        <dl className="space-y-3.5">
          <Row label="Status">
            <select
              aria-label="Status"
              className={selectCls}
              disabled={!canWrite}
              value={ticket.status}
              title={STATUS_HELP[ticket.status]}
              onChange={(e) => save({ status: e.target.value as Ticket['status'] })}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Priority">
            <select
              aria-label="Priority"
              className={selectCls}
              disabled={!canWrite}
              value={ticket.priority}
              onChange={(e) => save({ priority: e.target.value as Ticket['priority'] })}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Assignee">
            <div className="flex gap-2">
              <select
                aria-label="Assignee"
                className={selectCls}
                disabled={!canWrite || lockedAssignee}
                value={ticket.assigneeId ?? ''}
                onChange={(e) => save({ assigneeId: e.target.value || null })}
              >
                <option value="">Unassigned</option>
                {assignable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.id === user.id ? `${u.name} (me)` : u.name}
                  </option>
                ))}
              </select>
              {canWrite && !ticket.assigneeId && (
                <button
                  type="button"
                  onClick={() => save({ assigneeId: user.id }, 'Ticket assigned to you')}
                  className="text-brand-600 hover:bg-brand-50 shrink-0 rounded-lg px-2 text-xs font-semibold"
                >
                  Take it
                </button>
              )}
            </div>
          </Row>
          <Row label="Team">
            <select
              aria-label="Team"
              className={selectCls}
              disabled={!canWrite}
              value={ticket.teamId ?? ''}
              onChange={(e) => save({ teamId: e.target.value || null })}
            >
              <option value="">No team</option>
              {(teams.data ?? [])
                .filter((t) => t.isActive || t.id === ticket.teamId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </Row>
          <Row label="Tags">
            <TagEditor
              key={ticket.tags.join(',')}
              tags={ticket.tags}
              disabled={!canWrite}
              onSave={(tags) => save({ tags })}
            />
          </Row>
        </dl>
      </section>

      <section aria-labelledby="details-h" className="space-y-3 border-t border-slate-100 pt-5">
        <h2 id="details-h" className="text-sm font-bold">
          Details
        </h2>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
          <Row label="Channel">{CHANNEL_LABEL[ticket.channel]}</Row>
          <Row label="Category">{ticket.category ? humanize(ticket.category) : '—'}</Row>
          <Row label="Created">
            <span title={formatDateTime(ticket.createdAt)}>{formatRelative(ticket.createdAt)}</span>
          </Row>
          <Row label="First response">
            {ticket.firstResponseAt ? (
              formatDuration(frtMinutes)
            ) : ticket.slaDueAt && ticket.status === 'open' ? (
              <SlaBadge ticket={ticket} />
            ) : (
              'Not yet'
            )}
          </Row>
          {ticket.resolvedAt && <Row label="Resolved">{formatRelative(ticket.resolvedAt)}</Row>}
          {ticket.csatRating && (
            <div className="col-span-2">
              <Row label="Customer rating">
                <span className="flex items-center gap-0.5" aria-label={`${ticket.csatRating} out of 5`}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      aria-hidden
                      className={
                        i <= ticket.csatRating!
                          ? 'h-4 w-4 fill-amber-400 text-amber-400'
                          : 'h-4 w-4 text-slate-300'
                      }
                    />
                  ))}
                </span>
                {ticket.csatComment && <p className="mt-1 text-slate-600 italic">“{ticket.csatComment}”</p>}
              </Row>
            </div>
          )}
        </dl>
      </section>

      <TicketAiUsage ticketId={ticket.id} />

      <CustomerCard ticket={ticket} />
    </div>
  );
}

/** Tokens the AI has spent on this ticket (auto-replies, drafts, summaries); hidden until it has any. */
function TicketAiUsage({ ticketId }: { ticketId: string }) {
  const { data } = useTicketAiUsage(ticketId);
  if (!data || data.totals.requests === 0) return null;
  const t = data.totals;
  return (
    <section aria-labelledby="ai-usage-h" className="space-y-3 border-t border-slate-100 pt-5">
      <h2 id="ai-usage-h" className="text-sm font-bold">
        AI usage
      </h2>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
        <Row label="Tokens used">
          <span className="font-semibold tabular-nums">{formatNumber(t.inputTokens + t.outputTokens)}</span>
        </Row>
        <Row label="AI requests">
          <span className="tabular-nums">
            {t.requests}
            {t.failed > 0 && <span className="text-xs text-amber-700"> · {t.failed} failed</span>}
          </span>
        </Row>
        <Row label="Input tokens">
          <span className="tabular-nums">{formatNumber(t.inputTokens)}</span>
        </Row>
        <Row label="Output tokens">
          <span className="tabular-nums">{formatNumber(t.outputTokens)}</span>
        </Row>
      </dl>
      {data.byFeature.length > 1 && (
        <ul className="space-y-1 text-xs text-slate-500">
          {data.byFeature.map((f) => (
            <li key={f.feature} className="flex justify-between gap-2">
              <span>{AI_FEATURE_LABEL[f.feature] ?? humanize(f.feature)}</span>
              <span className="tabular-nums">{formatTokens(f.inputTokens + f.outputTokens)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TagEditor({
  tags,
  disabled,
  onSave,
}: {
  tags: string[];
  disabled: boolean;
  onSave: (tags: string[]) => void;
}) {
  const [value, setValue] = useState(tags.join(', '));
  const commit = () => {
    const next = [
      ...new Set(
        value
          .split(/[\s,]+/)
          .map((t) =>
            t
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9_-]/g, ''),
          )
          .filter(Boolean),
      ),
    ];
    if (next.join(',') !== tags.join(',')) onSave(next);
    setValue(next.join(', '));
  };
  return (
    <input
      aria-label="Tags"
      disabled={disabled}
      value={value}
      placeholder="payout, instagram"
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
      className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm disabled:bg-slate-50"
    />
  );
}

function CustomerCard({ ticket }: { ticket: Ticket }) {
  const { can } = useAuth();
  const customer = useCustomer(can('read', 'Customer') ? ticket.customerId : undefined);
  const history = useTicketsInfinite({ customerId: ticket.customerId });
  const others = (history.data?.pages[0]?.data ?? []).filter((t) => t.id !== ticket.id).slice(0, 4);
  const c = customer.data;
  const name = c?.name ?? ticket.customer?.name ?? 'Customer';

  return (
    <section aria-labelledby="cust-h" className="space-y-3 border-t border-slate-100 pt-5">
      <h2 id="cust-h" className="text-sm font-bold">
        Customer
      </h2>
      <div className="flex items-center gap-3">
        <Avatar name={name} src={c?.avatarUrl ?? ticket.customer?.avatarUrl} />
        <div className="min-w-0">
          {can('read', 'Customer') ? (
            <Link
              to={`/customers/${ticket.customerId}`}
              className="hover:text-brand-700 block truncate font-semibold"
            >
              {name}
            </Link>
          ) : (
            <p className="truncate font-semibold">{name}</p>
          )}
          <p className="truncate text-xs text-slate-500">
            {c?.email ?? ticket.customer?.email ?? 'No email'}
          </p>
        </div>
      </div>
      <VerifiedBadge at={c?.identityVerifiedAt ?? ticket.customer?.identityVerifiedAt} />
      {c && (
        <>
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="brand">{humanize(c.type)}</Badge>
            {c.plan && <Badge>{humanize(c.plan)}</Badge>}
            {c.tags.map((t) => (
              <Badge key={t} tone={t === 'vip' ? 'accent' : 'neutral'}>
                {t}
              </Badge>
            ))}
          </div>
          <dl className="grid grid-cols-2 gap-3">
            <Row label="City">{c.city ?? '—'}</Row>
            <Row label="Phone">{c.phone ?? '—'}</Row>
            <Row label="Tickets">
              {c.ticketCount} total · {c.openTicketCount} open
            </Row>
            <Row label="Last seen">{formatRelative(c.lastSeenAt)}</Row>
          </dl>
          <KollabrAttributes customer={c} />
          {c.notes && (
            <p className="rounded-lg bg-slate-50 p-2.5 text-xs whitespace-pre-line text-slate-600">
              {c.notes}
            </p>
          )}
        </>
      )}
      {others.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-slate-500">Recent tickets</p>
          <ul className="space-y-1">
            {others.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/inbox/${t.id}`}
                  className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50"
                >
                  <span className="truncate text-slate-700">
                    <span className="text-slate-400">#{t.number}</span> {t.subject}
                  </span>
                  <StatusBadge status={t.status} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
